import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Phone, Mail, MapPin, Globe, Shield, Clock, Send, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";

const Contact = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white">
      {/* Navigation */}
      <nav className="bg-white/90 backdrop-blur-sm border-b border-blue-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center space-x-2">
              <Shield className="h-8 w-8 text-blue-600" />
              <span className="text-2xl font-bold text-gray-900">Fremont Tech</span>
              <span className="text-sm text-gray-500">프리몬테크</span>
            </Link>
            <div className="hidden md:flex space-x-8">
              <Link to="/about" className="text-gray-700 hover:text-blue-600 transition-colors">ABOUT US</Link>
              <Link to="/services" className="text-gray-700 hover:text-blue-600 transition-colors">WHAT WE DO</Link>
              <Link to="/contact" className="text-blue-600 font-semibold">CONTACT US</Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Back Button */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
        <Link to="/" className="inline-flex items-center text-blue-600 hover:text-blue-700 mb-8">
          <ArrowLeft className="h-4 w-4 mr-2" />
          홈으로 돌아가기
        </Link>
      </div>

      {/* Hero Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center">
          <Badge className="mb-4 bg-blue-100 text-blue-800 hover:bg-blue-200">
            Contact Us
          </Badge>
          <h1 className="text-5xl font-bold text-gray-900 mb-6">
            <span className="text-blue-600">언제든지</span> 연락주세요
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            무료 상담을 통해 귀하의 프로젝트에 대해 자세히 이야기해보세요<br />
            "한 번 고객은 영원한 친구입니다"
          </p>
        </div>
      </section>

      {/* Contact Information */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Contact Form */}
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-8">무료 상담 신청</h2>
              <Card className="p-8">
                <form className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="name">이름 *</Label>
                      <Input id="name" placeholder="성함을 입력해주세요" className="mt-1" />
                    </div>
                    <div>
                      <Label htmlFor="company">회사명</Label>
                      <Input id="company" placeholder="회사명을 입력해주세요" className="mt-1" />
                    </div>
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="phone">연락처 *</Label>
                      <Input id="phone" placeholder="010-0000-0000" className="mt-1" />
                    </div>
                    <div>
                      <Label htmlFor="email">이메일 *</Label>
                      <Input id="email" type="email" placeholder="example@email.com" className="mt-1" />
                    </div>
                  </div>
                  
                  <div>
                    <Label htmlFor="service">관심 서비스</Label>
                    <select id="service" className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500">
                      <option value="">서비스를 선택해주세요</option>
                      <option value="website">웹사이트 제작</option>
                      <option value="app">앱 개발</option>
                      <option value="cctv">CCTV 상담 및 설치</option>
                      <option value="multiple">여러 서비스</option>
                      <option value="other">기타</option>
                    </select>
                  </div>
                  
                  <div>
                    <Label htmlFor="budget">예산 범위</Label>
                    <select id="budget" className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500">
                      <option value="">예산 범위를 선택해주세요</option>
                      <option value="under-500">500만원 미만</option>
                      <option value="500-1000">500만원 - 1,000만원</option>
                      <option value="1000-3000">1,000만원 - 3,000만원</option>
                      <option value="over-3000">3,000만원 이상</option>
                      <option value="discuss">상담 후 결정</option>
                    </select>
                  </div>
                  
                  <div>
                    <Label htmlFor="message">프로젝트 상세 내용 *</Label>
                    <Textarea 
                      id="message" 
                      placeholder="프로젝트에 대해 자세히 설명해주세요. 목적, 기능, 일정 등을 포함해주시면 더 정확한 상담이 가능합니다."
                      className="mt-1 min-h-[120px]"
                    />
                  </div>
                  
                  <Button className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3">
                    <Send className="mr-2 h-5 w-5" />
                    무료 상담 신청하기
                  </Button>
                  
                  <p className="text-sm text-gray-500 text-center">
                    * 표시된 항목은 필수 입력 사항입니다.<br />
                    보통 24시간 이내에 연락드립니다.
                  </p>
                </form>
              </Card>
            </div>

            {/* Contact Details */}
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-8">연락처 정보</h2>
              
              <div className="space-y-8">
                <Card className="p-6">
                  <div className="flex items-start space-x-4">
                    <div className="bg-blue-100 rounded-full p-3">
                      <Phone className="h-6 w-6 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">전화 상담</h3>
                      <p className="text-gray-600 mb-2">직접 통화를 통한 빠른 상담</p>
                      <p className="text-xl font-bold text-blue-600">010-2864-1942</p>
                      <p className="text-sm text-gray-500 mt-1">평일 09:00 - 18:00</p>
                    </div>
                  </div>
                </Card>

                <Card className="p-6">
                  <div className="flex items-start space-x-4">
                    <div className="bg-blue-100 rounded-full p-3">
                      <Mail className="h-6 w-6 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">이메일</h3>
                      <p className="text-gray-600 mb-2">자세한 문의사항을 이메일로</p>
                      <p className="text-xl font-bold text-blue-600">hyungwoo@naver.com</p>
                      <p className="text-sm text-gray-500 mt-1">24시간 이내 답변</p>
                    </div>
                  </div>
                </Card>

                <Card className="p-6">
                  <div className="flex items-start space-x-4">
                    <div className="bg-blue-100 rounded-full p-3">
                      <MapPin className="h-6 w-6 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">오피스 주소</h3>
                      <p className="text-gray-600 mb-2">직접 방문 상담 가능</p>
                      <p className="text-lg text-gray-900">
                        대전 중구 대종로 550번길 5<br />
                        유원오피스텔 1008호
                      </p>
                      <p className="text-sm text-gray-500 mt-1">사전 예약 필수</p>
                    </div>
                  </div>
                </Card>

                <Card className="p-6">
                  <div className="flex items-start space-x-4">
                    <div className="bg-blue-100 rounded-full p-3">
                      <Globe className="h-6 w-6 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">웹사이트</h3>
                      <p className="text-gray-600 mb-2">기존 포트폴리오 확인</p>
                      <p className="text-xl font-bold text-blue-600">newvision.co.kr</p>
                      <p className="text-sm text-gray-500 mt-1">CCTV 전문 사이트</p>
                    </div>
                  </div>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Business Hours */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-blue-50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">운영 시간</h2>
            <p className="text-lg text-gray-600">고객 상담 및 서비스 제공 시간</p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8">
            <Card className="p-8 text-center">
              <Clock className="h-12 w-12 text-blue-600 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-4">평일 운영시간</h3>
              <div className="space-y-2 text-gray-600">
                <p><strong>월요일 - 금요일:</strong> 09:00 - 18:00</p>
                <p><strong>점심시간:</strong> 12:00 - 13:00</p>
                <p className="text-sm text-blue-600 mt-4">
                  * 긴급한 경우 시간 외에도 연락 가능
                </p>
              </div>
            </Card>
            
            <Card className="p-8 text-center">
              <Shield className="h-12 w-12 text-blue-600 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-4">응급 서비스</h3>
              <div className="space-y-2 text-gray-600">
                <p><strong>CCTV 장애:</strong> 24시간 대응</p>
                <p><strong>시스템 오류:</strong> 즉시 지원</p>
                <p className="text-sm text-blue-600 mt-4">
                  * 기존 고객 대상 응급 지원 서비스
                </p>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">자주 묻는 질문</h2>
            <p className="text-lg text-gray-600">고객들이 자주 문의하시는 내용들</p>
          </div>
          
          <div className="space-y-6">
            <Card className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Q. 프로젝트 진행 기간은 얼마나 걸리나요?
              </h3>
              <p className="text-gray-600">
                A. 프로젝트 규모에 따라 다르지만, 일반적으로 웹사이트는 2-4주, 앱 개발은 2-3개월, 
                CCTV 설치는 1-2일 정도 소요됩니다. 정확한 일정은 상담 후 안내드립니다.
              </p>
            </Card>
            
            <Card className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Q. 유지보수 서비스도 제공하나요?
              </h3>
              <p className="text-gray-600">
                A. 네, 모든 프로젝트에 대해 지속적인 유지보수 서비스를 제공합니다. 
                기본 유지보수 기간과 유료 연장 서비스 모두 가능합니다.
              </p>
            </Card>
            
            <Card className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Q. 전국 어디든 서비스가 가능한가요?
              </h3>
              <p className="text-gray-600">
                A. 네, 전국 어디든 방문 서비스가 가능합니다. CCTV 설치의 경우 
                직접 방문하여 현장 조사부터 설치, 사후관리까지 제공합니다.
              </p>
            </Card>
            
            <Card className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Q. 상담비용이 있나요?
              </h3>
              <p className="text-gray-600">
                A. 모든 상담은 무료입니다. 전화, 이메일, 방문 상담 모두 무료로 제공하며, 
                정확한 견적과 솔루션을 제안해드립니다.
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-blue-600">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            지금 바로 시작하세요
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            무료 상담을 통해 귀하의 프로젝트를 성공으로 이끌어보세요
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" variant="secondary" className="px-8 py-3">
              <Phone className="mr-2 h-5 w-5" />
              010-2864-1942
            </Button>
            <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-blue-600 px-8 py-3">
              <Mail className="mr-2 h-5 w-5" />
              이메일 보내기
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <Link to="/" className="flex items-center space-x-2 mb-4">
                <Shield className="h-6 w-6 text-blue-400" />
                <span className="text-xl font-bold">Fremont Tech</span>
                <span className="text-sm text-gray-400">프리몬테크</span>
              </Link>
              <p className="text-gray-400 mb-4">
                신뢰할 수 있는 기술 파트너로서 고객의 성공을 위한<br />
                최고의 솔루션을 제공합니다.
              </p>
              <p className="text-sm text-gray-500">
                © 2024 Fremont Tech. All rights reserved.
              </p>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">연락처</h3>
              <div className="space-y-2 text-gray-400">
                <div className="flex items-center space-x-2">
                  <Phone className="h-4 w-4" />
                  <span>010-2864-1942</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Mail className="h-4 w-4" />
                  <span>hyungwoo@naver.com</span>
                </div>
                <div className="flex items-center space-x-2">
                  <MapPin className="h-4 w-4" />
                  <span>대전 중구 대종로 550번길 5 유원오피스텔 1008호</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Globe className="h-4 w-4" />
                  <span>newvision.co.kr</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Contact;